import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

/**
 * Xtellar Design System button, adapted for an operations app.
 *
 * Pill-shaped and semibold, as the DS specifies. Heights stay compact because
 * these sit in toolbars and table rows.
 *
 * - `default` is the DS "dark" pill: neutral-950, for routine actions.
 * - `brand` is the DS "primary": red, for the single main action on a screen.
 *   Using it for everything is how red stops meaning anything.
 * - `destructive` is a red outline, so deleting never looks like the brand CTA.
 * - `field` is a button that is really a form control, such as a combobox
 *   trigger. It takes the input's shape so it lines up with the fields beside it.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/85',
        brand: 'bg-brand text-brand-foreground hover:bg-brand/90',
        destructive:
          'border border-destructive/50 bg-transparent text-destructive hover:bg-destructive/10 focus-visible:ring-destructive/30',
        outline:
          'border border-border-strong bg-transparent hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-foreground underline-offset-4 hover:underline',
        field:
          'rounded-md border border-input bg-transparent font-normal shadow-xs hover:bg-accent/50 dark:bg-input/30 dark:hover:bg-input/50',
      },
      size: {
        default: 'h-9 px-4 has-[>svg]:px-3.5',
        sm: 'h-8 gap-1.5 px-3.5 has-[>svg]:px-3',
        // For actions inside a table row, so a button never sets the row's height.
        xs: "h-7 gap-1 px-2.5 text-xs has-[>svg]:px-2 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-10 px-6 has-[>svg]:px-5',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-xs': "size-7 [&_svg:not([class*='size-'])]:size-3.5",
        'icon-lg': 'size-10',
      },
    },
    // After `size`, so a field keeps the input's padding at every height.
    compoundVariants: [{ variant: 'field', class: 'px-3 has-[>svg]:px-3' }],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
